import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { pdf, Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';

const PurchaseReport = () => {
  const apiUrl = process.env.REACT_APP_API_BASE_URL;

  // State for purchase invoices and loading
  const [purchaseInvoices, setPurchaseInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for suppliers list
  const [suppliers, setSuppliers] = useState([]);

  // State for filters
  const [filters, setFilters] = useState({
    poNumber: '',
    supplierName: '',
    siteName: '',
    invoiceNumber: '',
    dateFrom: '',
    dateTo: '',
    status: '', // '' for all, 'Paid', 'Unpaid'
  });

  // State for filtered results
  const [filteredPurchaseInvoices, setFilteredPurchaseInvoices] = useState([]);

  // Fetch purchase invoices and suppliers on component mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch purchase invoices
        const posResponse = await axios.get(`${apiUrl}/api/pos`);
        const responseData = posResponse.data;
        
        // Handle different API response structures
        let posData = [];
        if (Array.isArray(responseData)) {
          posData = responseData;
        } else if (responseData?.data) {
          // Check if it's a paginated response with nested data
          if (Array.isArray(responseData.data)) {
            posData = responseData.data;
          } else if (responseData.data?.data && Array.isArray(responseData.data.data)) {
            posData = responseData.data.data;
          }
        }
        
        setPurchaseInvoices(posData);
        setFilteredPurchaseInvoices(posData);

        // Fetch suppliers for dropdown
        const suppliersResponse = await axios.get(`${apiUrl}/api/suppliers`);
        const suppliersData = Array.isArray(suppliersResponse.data)
          ? suppliersResponse.data
          : suppliersResponse.data.data || [];
        setSuppliers(suppliersData);

        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load purchase invoice data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [apiUrl]);

  // Apply filters whenever they change or purchase invoices change
  useEffect(() => {
    applyFilters();
  }, [filters, purchaseInvoices]);

  // Helper function to get supplier name from nested object
  const getSupplierName = (po) => {
    return po.supplier?.name || '';
  };

  // Helper function to get site name (customer name) from nested object
  const getSiteName = (po) => {
    return po.customer?.name || '';
  };

  // Helper function to get PO number
  const getPONumber = (po) => {
    return po.po_no || '';
  };

  // Helper function to get reference/invoice number
  const getReferenceNumber = (po) => {
    return po.reference_no || '';
  };

  // Helper function to calculate PO totals from line items
  const calculatePOTotals = (po) => {
    if (!po.plines || po.plines.length === 0) {
      return { subtotal: 0, tax: 0, total: 0 };
    }

    const subtotal = po.plines.reduce((sum, line) => sum + (parseFloat(line.line_subtotal_after_discount) || 0), 0);
    const tax = po.plines.reduce((sum, line) => sum + (parseFloat(line.line_vat) || 0), 0);
    const total = po.plines.reduce((sum, line) => sum + (parseFloat(line.line_total) || 0), 0);

    return { subtotal, tax, total };
  };

  // Apply filters to purchase invoices
  const applyFilters = () => {
    let filtered = [...purchaseInvoices];

    // Filter by PO number
    if (filters.poNumber) {
      filtered = filtered.filter((po) =>
        getPONumber(po)
          .toLowerCase()
          .includes(filters.poNumber.toLowerCase())
      );
    }

    // Filter by supplier name
    if (filters.supplierName) {
      filtered = filtered.filter((po) =>
        getSupplierName(po)
          .toLowerCase()
          .includes(filters.supplierName.toLowerCase())
      );
    }

    // Filter by site name
    if (filters.siteName) {
      filtered = filtered.filter((po) =>
        getSiteName(po)
          .toLowerCase()
          .includes(filters.siteName.toLowerCase())
      );
    }

    // Filter by Invoice number (reference_no)
    if (filters.invoiceNumber) {
      filtered = filtered.filter((po) =>
        getReferenceNumber(po)
          ?.toLowerCase()
          .includes(filters.invoiceNumber.toLowerCase())
      );
    }

    // Filter by date range
    if (filters.dateFrom) {
      filtered = filtered.filter((po) => {
        const poDate = new Date(po.po_date);
        const fromDate = new Date(filters.dateFrom);
        return poDate >= fromDate;
      });
    }

    if (filters.dateTo) {
      filtered = filtered.filter((po) => {
        const poDate = new Date(po.po_date);
        const toDate = new Date(filters.dateTo);
        toDate.setHours(23, 59, 59, 999); // Include entire day
        return poDate <= toDate;
      });
    }

    // Filter by status
    if (filters.status) {
      filtered = filtered.filter(
        (po) => po.status === filters.status
      );
    }

    setFilteredPurchaseInvoices(filtered);
  };

  // Handle filter input change
  const handleFilterChange = (field, value) => {
    setFilters({
      ...filters,
      [field]: value,
    });
  };

  // Reset all filters
  const resetFilters = () => {
    setFilters({
      poNumber: '',
      supplierName: '',
      siteName: '',
      invoiceNumber: '',
      dateFrom: '',
      dateTo: '',
      status: '',
    });
  };

  // Export table to PDF
  const exportToPDF = async () => {
    try {
      const pdfDocument = React.createElement(PurchaseReportPDFDocument);
      const blob = await pdf(pdfDocument).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `PurchaseReport_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF export failed:', err);
      alert('Unable to export PDF. Please try again.');
    }
  };

  // PDF Styles
  const pdfStyles = StyleSheet.create({
    page: {
      padding: 30,
      fontFamily: 'Helvetica',
    },
    title: {
      fontSize: 18,
      fontWeight: 'bold',
      marginBottom: 15,
      color: '#0891b2',
    },
    subtitle: {
      fontSize: 10,
      color: '#666',
      marginBottom: 20,
    },
    table: {
      width: '100%',
      marginBottom: 20,
    },
    tableHeader: {
      flexDirection: 'row',
      backgroundColor: '#f3f4f6',
      fontWeight: 'bold',
      fontSize: 9,
      borderBottomWidth: 1,
      borderBottomColor: '#000',
      paddingVertical: 6,
    },
    tableRow: {
      flexDirection: 'row',
      fontSize: 8,
      borderBottomWidth: 1,
      borderBottomColor: '#e5e7eb',
      paddingVertical: 6,
    },
    tableTotalRow: {
      flexDirection: 'row',
      backgroundColor: '#f9fafb',
      fontWeight: 'bold',
      fontSize: 9,
      borderTopWidth: 2,
      borderTopColor: '#000',
      borderBottomWidth: 1,
      borderBottomColor: '#000',
      paddingVertical: 6,
    },
    col1: {
      width: '11%',
      paddingHorizontal: 6,
      overflow: 'hidden',
    },
    col2: {
      width: '11%',
      paddingHorizontal: 6,
      overflow: 'hidden',
    },
    col3: {
      width: '15%',
      paddingHorizontal: 6,
      overflow: 'hidden',
    },
    col4: {
      width: '15%',
      paddingHorizontal: 6,
      overflow: 'hidden',
    },
    col5: {
      width: '11%',
      paddingHorizontal: 6,
      overflow: 'hidden',
    },
    col6: {
      width: '12%',
      paddingHorizontal: 6,
      overflow: 'hidden',
      textAlign: 'right',
    },
    col7: {
      width: '12%',
      paddingHorizontal: 6,
      overflow: 'hidden',
      textAlign: 'right',
    },
    col8: {
      width: '12%',
      paddingHorizontal: 6,
      overflow: 'hidden',
      textAlign: 'right',
    },
    col9: {
      width: '11%',
      paddingHorizontal: 6,
      overflow: 'hidden',
    },
    footerText: {
      fontSize: 9,
      color: '#666',
      marginTop: 10,
    },
  });

  // PDF Document Component
  const PurchaseReportPDFDocument = () => {
    const hasDateFilter = filters.dateFrom || filters.dateTo;
    const dateRangeText = hasDateFilter
      ? `${filters.dateFrom ? formatDate(filters.dateFrom) : 'Start'} - ${filters.dateTo ? formatDate(filters.dateTo) : 'End'}`
      : '';

    return (
      <Document>
        <Page size="A4" orientation="landscape" style={pdfStyles.page}>
          <Text style={pdfStyles.title}>Purchase Invoice Report</Text>
          <View style={{ marginBottom: 20 }}>
            <Text style={pdfStyles.subtitle}>
              Generated on {new Date().toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              })}
            </Text>
            {hasDateFilter && (
              <Text style={{ fontSize: 10, color: '#0891b2', fontWeight: 'bold', marginTop: 5 }}>
                Period: {dateRangeText}
              </Text>
            )}
          </View>

          <View style={pdfStyles.table}>
            {/* Table Header */}
            <View style={pdfStyles.tableHeader}>
              <Text style={pdfStyles.col1}>PO #</Text>
              <Text style={pdfStyles.col2}>Reference #</Text>
              <Text style={pdfStyles.col3}>Supplier</Text>
              <Text style={pdfStyles.col4}>Site Name</Text>
              <Text style={pdfStyles.col5}>Date</Text>
              <Text style={pdfStyles.col6}>Subtotal</Text>
              <Text style={pdfStyles.col7}>Tax</Text>
              <Text style={pdfStyles.col8}>Total</Text>
              <Text style={pdfStyles.col9}>Status</Text>
            </View>

            {/* Table Rows */}
            {filteredPurchaseInvoices.map((po) => {
              const poTotals = calculatePOTotals(po);
              return (
                <View key={po.id} style={pdfStyles.tableRow}>
                  <Text style={pdfStyles.col1}>{getPONumber(po)}</Text>
                  <Text style={pdfStyles.col2}>{getReferenceNumber(po)}</Text>
                  <Text style={pdfStyles.col3}>{getSupplierName(po)}</Text>
                  <Text style={pdfStyles.col4}>{getSiteName(po)}</Text>
                  <Text style={pdfStyles.col5}>{formatDate(po.po_date)}</Text>
                  <Text style={pdfStyles.col6}>{formatCurrency(poTotals.subtotal)}</Text>
                  <Text style={pdfStyles.col7}>{formatCurrency(poTotals.tax)}</Text>
                  <Text style={pdfStyles.col8}>{formatCurrency(poTotals.total)}</Text>
                  <Text style={pdfStyles.col9}>{po.status}</Text>
                </View>
              );
            })}

            {/* Totals Row */}
            <View style={pdfStyles.tableTotalRow}>
              <Text style={pdfStyles.col1}></Text>
              <Text style={pdfStyles.col2}></Text>
              <Text style={pdfStyles.col3}></Text>
              <Text style={pdfStyles.col4}></Text>
              <Text style={pdfStyles.col5}></Text>
              <Text style={pdfStyles.col6}>{formatCurrency(totals.subtotal)}</Text>
              <Text style={pdfStyles.col7}>{formatCurrency(totals.tax)}</Text>
              <Text style={pdfStyles.col8}>{formatCurrency(totals.total)}</Text>
              <Text style={pdfStyles.col9}></Text>
            </View>
          </View>

          <Text style={pdfStyles.footerText}>
            Showing {filteredPurchaseInvoices.length} of {purchaseInvoices.length} purchase invoices
          </Text>
        </Page>
      </Document>
    );
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
    }).format(amount || 0);
  };

  // Calculate totals for filtered results
  const calculateTotals = () => {
    let subtotal = 0;
    let tax = 0;
    let total = 0;

    if (Array.isArray(filteredPurchaseInvoices) && filteredPurchaseInvoices.length > 0) {
      filteredPurchaseInvoices.forEach((po) => {
        const poTotals = calculatePOTotals(po);
        subtotal += poTotals.subtotal;
        tax += poTotals.tax;
        total += poTotals.total;
      });
    }

    return { subtotal, tax, total };
  };

  const totals = calculateTotals();

  if (loading) {
    return (
      <div className="flex items-center justify-center w-full min-h-screen">
        <div className="text-lg text-gray-600">Loading purchase invoice data...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Purchase Invoice Report</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-2">
            View and search all purchase invoices
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {/* Filter Section */}
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Filters</h2>

          {/* Desktop Filters - Grid View */}
          <div className="hidden lg:grid lg:grid-cols-7 gap-4 mb-4">
            {/* PO Number Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                PO Number
              </label>
              <input
                type="text"
                placeholder="Search PO #..."
                value={filters.poNumber}
                onChange={(e) =>
                  handleFilterChange('poNumber', e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Invoice Number Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ref #
              </label>
              <input
                type="text"
                placeholder="Search invoice..."
                value={filters.invoiceNumber}
                onChange={(e) =>
                  handleFilterChange('invoiceNumber', e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Supplier Name Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Supplier
              </label>
              <input
                type="text"
                placeholder="Search supplier..."
                value={filters.supplierName}
                onChange={(e) =>
                  handleFilterChange('supplierName', e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Site Name Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Site Name
              </label>
              <input
                type="text"
                placeholder="Search site..."
                value={filters.siteName}
                onChange={(e) =>
                  handleFilterChange('siteName', e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Date From Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date From
              </label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Date To Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date To
              </label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All</option>
                <option value="Paid">Paid</option>
                <option value="Unpaid">Unpaid</option>
              </select>
            </div>
          </div>

          {/* Mobile/Tablet Filters - Collapsible/Stacked View */}
          <div className="lg:hidden space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  PO Number
                </label>
                <input
                  type="text"
                  placeholder="Search PO #..."
                  value={filters.poNumber}
                  onChange={(e) => handleFilterChange('poNumber', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ref #
                </label>
                <input
                  type="text"
                  placeholder="Search invoice..."
                  value={filters.invoiceNumber}
                  onChange={(e) => handleFilterChange('invoiceNumber', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Supplier
                </label>
                <input
                  type="text"
                  placeholder="Search supplier..."
                  value={filters.supplierName}
                  onChange={(e) => handleFilterChange('supplierName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Site Name
                </label>
                <input
                  type="text"
                  placeholder="Search site..."
                  value={filters.siteName}
                  onChange={(e) => handleFilterChange('siteName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date From
                </label>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date To
                </label>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">All</option>
                  <option value="Paid">Paid</option>
                  <option value="Unpaid">Unpaid</option>
                </select>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            {/* Reset Button */}
            <button
              onClick={resetFilters}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors text-sm sm:text-base"
            >
              Reset Filters
            </button>

            {/* Export Button */}
            <button
              onClick={exportToPDF}
              disabled={filteredPurchaseInvoices.length === 0}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed text-sm sm:text-base"
            >
              Export to PDF
            </button>
          </div>

          {/* Results Count */}
          <div className="mt-4 text-sm text-gray-600">
            Showing {filteredPurchaseInvoices.length} of {purchaseInvoices.length} purchase invoices
          </div>
        </div>

        {/* Desktop Results Table - Scrollable */}
        <div className="hidden lg:block bg-white rounded-lg shadow-md overflow-hidden">
          {filteredPurchaseInvoices.length > 0 ? (
            <>
              {/* Horizontal scroll container */}
              <div className="overflow-x-auto">
                {/* Vertical scroll container */}
                <div className="max-h-[600px] overflow-y-auto">
                  <table className="w-full min-w-[1200px]">
                    <thead className="bg-gray-100 border-b border-gray-200 sticky top-0 z-10">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider sticky left-0 bg-gray-100 z-20">
                          PO #
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                          Ref #
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                          Supplier
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                          Site Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                          Subtotal
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                          Tax
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                          Total
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredPurchaseInvoices.map((po) => {
                        const poTotals = calculatePOTotals(po);
                        return (
                        <tr
                          key={po.id}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-6 py-4 whitespace-nowrap sticky left-0 bg-white z-10">
                            <span className="font-medium text-blue-600">
                              {getPONumber(po)}
                            </span>
                            </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="font-medium text-gray-900">
                              {getReferenceNumber(po)}
                            </span>
                            </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getSupplierName(po)}
                            </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getSiteName(po)}
                            </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {formatDate(po.po_date)}
                            </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                            {formatCurrency(poTotals.subtotal)}
                            </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                            {formatCurrency(poTotals.tax)}
                            </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right font-medium">
                            {formatCurrency(poTotals.total)}
                            </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`px-3 py-1 rounded-full text-sm font-medium ${
                                po.status === 'Paid'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}
                            >
                              {po.status}
                            </span>
                            </td>
                         </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Totals Footer */}
              <div className="bg-gray-50 border-t border-gray-200 px-6 py-4">
                <div className="flex justify-end gap-8 font-semibold text-sm sm:text-base">
                  <div className="text-gray-700">
                    Subtotal: {formatCurrency(totals.subtotal)}
                  </div>
                  <div className="text-gray-700">
                    VAT: {formatCurrency(totals.tax)}
                  </div>
                  <div className="text-lg text-gray-900">
                    Total: {formatCurrency(totals.total)}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="p-8 text-center text-gray-500">
              No purchase invoices found matching your filters.
            </div>
          )}
        </div>

        {/* Mobile/Tablet Card View */}
        <div className="lg:hidden space-y-3">
          {filteredPurchaseInvoices.length > 0 ? (
            <>
              {filteredPurchaseInvoices.map((po) => {
                const poTotals = calculatePOTotals(po);
                return (
                  <div key={po.id} className="bg-white rounded-lg shadow-md p-4 border border-gray-100">
                    {/* Header with PO # and Status */}
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="text-xs text-gray-500">PO #</p>
                        <p className="font-semibold text-blue-600 text-sm">
                          {getPONumber(po)}
                        </p>
                      </div>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          po.status === 'Paid'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {po.status}
                      </span>
                    </div>

                    {/* Reference Number */}
                    <div className="mb-2">
                      <p className="text-xs text-gray-500">Ref #</p>
                      <p className="text-sm text-gray-900">{getReferenceNumber(po)}</p>
                    </div>

                    {/* Supplier and Site */}
                    <div className="grid grid-cols-2 gap-3 mb-2">
                      <div>
                        <p className="text-xs text-gray-500">Supplier</p>
                        <p className="text-sm text-gray-900">{getSupplierName(po)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Site Name</p>
                        <p className="text-sm text-gray-900">{getSiteName(po)}</p>
                      </div>
                    </div>

                    {/* Date */}
                    <div className="mb-2">
                      <p className="text-xs text-gray-500">Date</p>
                      <p className="text-sm text-gray-600">{formatDate(po.po_date)}</p>
                    </div>

                    {/* Financial Info */}
                    <div className="grid grid-cols-3 gap-2 pt-2 border-t border-gray-100 mt-2">
                      <div>
                        <p className="text-xs text-gray-500">Subtotal</p>
                        <p className="text-sm font-semibold text-gray-900">
                          {formatCurrency(poTotals.subtotal)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">VAT</p>
                        <p className="text-sm font-semibold text-amber-600">
                          {formatCurrency(poTotals.tax)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Total</p>
                        <p className="text-sm font-semibold text-blue-600">
                          {formatCurrency(poTotals.total)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Mobile Totals Footer */}
              <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotal:</span>
                    <span className="font-semibold">{formatCurrency(totals.subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">VAT:</span>
                    <span className="font-semibold text-amber-600">{formatCurrency(totals.tax)}</span>
                  </div>
                  <div className="flex justify-between text-base pt-2 border-t border-gray-200">
                    <span className="font-bold text-gray-900">Total:</span>
                    <span className="font-bold text-blue-600">{formatCurrency(totals.total)}</span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">
              No purchase invoices found matching your filters.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PurchaseReport;